import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FileSignature, Lock, Paperclip, Send, Trash2 } from "lucide-react";
import {
  bugReportApi,
  AdminApiError,
  type BugReport,
  type BugStatus,
} from "../api";
import {
  CopyButton,
  Empty,
  Loading,
  Modal,
  STATUS_LABEL,
  StatusPill,
  Tabs,
  humanSize,
  timeAgo,
  useToast,
} from "../ui";

/* Tickets — operator bug reports. The list stays scannable (status, entity,
 * first line, freshness); everything else lives in the tabbed modal:
 * Overview | Conversation | Builds. */

const STATUSES: ("all" | BugStatus)[] = ["all", "open", "ack", "fixed", "wontfix"];

export function TicketsPage({ onAttentionChange }: { onAttentionChange?: () => void }) {
  const toast = useToast();
  const [tickets, setTickets] = useState<BugReport[] | null>(null);
  const [filter, setFilter] = useState("");
  const [status, setStatus] = useState<(typeof STATUSES)[number]>("all");
  const [openId, setOpenId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const r = await bugReportApi.list();
      setTickets(r.reports ?? []);
    } catch (e) {
      if (!(e instanceof AdminApiError && e.status === 401)) {
        toast("err", e instanceof AdminApiError ? e.message : "Failed to load tickets");
      }
      setTickets((t) => t ?? []);
    }
  }, [toast]);
  useEffect(() => {
    reload();
  }, [reload]);

  const rows = useMemo(() => {
    if (!tickets) return [];
    const f = filter.trim().toLowerCase();
    return tickets.filter((t) => {
      if (status !== "all" && t.status !== status) return false;
      if (!f) return true;
      return [t.entity, t.description, t.category, t.pluginVersion].some((v) =>
        (v || "").toLowerCase().includes(f),
      );
    });
  }, [tickets, filter, status]);

  const open = openId ? (tickets ?? []).find((t) => t.id === openId) ?? null : null;

  const patchLocal = (updated: BugReport) => {
    setTickets((prev) => (prev ?? []).map((t) => (t.id === updated.id ? updated : t)));
    onAttentionChange?.();
  };

  return (
    <div className="rise-in">
      <header>
        <h1 className="text-3xl font-semibold tracking-tight">Tickets</h1>
        <p className="mt-1 text-sm text-fg-mute">
          Bug reports from buyer Studios. Open one for the conversation and any attached builds.
        </p>
      </header>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <input
          className="field max-w-sm"
          placeholder="Filter by entity, text, version…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <div className="flex gap-1 rounded-(--radius-field) bg-ink-900 p-1">
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={`rounded-[7px] px-2.5 py-1 text-xs font-medium transition ${
                status === s ? "bg-ink-700 text-fg" : "text-fg-mute hover:text-fg"
              }`}
            >
              {s === "all" ? "All" : STATUS_LABEL[s]}
            </button>
          ))}
        </div>
      </div>

      <div className="card mt-4 overflow-hidden">
        {tickets === null ? (
          <Loading label="Loading tickets…" />
        ) : rows.length === 0 ? (
          <Empty title="No tickets here." hint="Reports land the moment a buyer sends one." />
        ) : (
          <ul className="divide-y divide-line">
            {rows.map((t) => (
              <li key={t.id}>
                <button
                  className="flex w-full items-center gap-3.5 px-5 py-3.5 text-left transition hover:bg-ink-850"
                  onClick={() => setOpenId(t.id)}
                >
                  <span
                    className={`h-2 w-2 shrink-0 rounded-full ${t.unread ? "bg-rose-500" : "bg-transparent"}`}
                  />
                  <StatusPill status={t.status} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm">{firstLine(t.description)}</div>
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-fg-faint">
                      <span className="font-medium text-fg-mute">{t.entity || "unknown"}</span>
                      <span>·</span>
                      <span>{t.category}</span>
                      {t.build && (
                        <>
                          <span>·</span>
                          <Paperclip className="h-3 w-3" />
                        </>
                      )}
                    </div>
                  </div>
                  <span className="shrink-0 text-xs text-fg-faint">{timeAgo(t.receivedAt)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {open && (
        <TicketModal
          ticket={open}
          onClose={() => setOpenId(null)}
          onPatched={patchLocal}
          onDeleted={(id) => {
            setTickets((prev) => (prev ?? []).filter((t) => t.id !== id));
            setOpenId(null);
            onAttentionChange?.();
          }}
        />
      )}
    </div>
  );
}

function firstLine(text: string): string {
  const l = (text || "").split("\n").map((s) => s.trim()).find(Boolean) ?? "(no description)";
  return l.length > 90 ? l.slice(0, 87) + "…" : l;
}

// ── Ticket modal — Overview | Conversation | Builds ───────────────────

type Tab = "overview" | "chat" | "builds";

function TicketModal({
  ticket,
  onClose,
  onPatched,
  onDeleted,
}: {
  ticket: BugReport;
  onClose: () => void;
  onPatched: (t: BugReport) => void;
  onDeleted: (id: string) => void;
}) {
  const [tab, setTab] = useState<Tab>("overview");

  // Opening marks it read (clears the unread dot for this ticket).
  const marked = useRef(false);
  useEffect(() => {
    if (marked.current || !ticket.unread) return;
    marked.current = true;
    bugReportApi.markRead(ticket.id).then(onPatched).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket.id]);

  const closed = ticket.status === "fixed" || ticket.status === "wontfix";

  return (
    <Modal
      wide
      onClose={onClose}
      title={
        <span className="flex items-center gap-2.5">
          {ticket.entity || "Ticket"} <StatusPill status={ticket.status} />
        </span>
      }
    >
      <Tabs<Tab>
        value={tab}
        onChange={setTab}
        tabs={[
          { id: "overview", label: "Overview" },
          {
            id: "chat",
            label: (
              <span className="inline-flex items-center gap-1.5">
                Conversation
                {(ticket.comments?.length ?? 0) > 0 && (
                  <span className="rounded bg-ink-600 px-1 text-[10px]">{ticket.comments!.length}</span>
                )}
              </span>
            ),
          },
          {
            id: "builds",
            label: (
              <span className="inline-flex items-center gap-1.5">
                Builds {ticket.build && <Paperclip className="h-3 w-3" />}
              </span>
            ),
          },
        ]}
      />

      <div className="mt-4 max-h-[60vh] overflow-y-auto pr-1">
        {tab === "overview" && (
          <OverviewTab ticket={ticket} onPatched={onPatched} onDeleted={onDeleted} />
        )}
        {tab === "chat" && <ChatTab ticket={ticket} closed={closed} onPatched={onPatched} />}
        {tab === "builds" && <BuildsTab ticket={ticket} onPatched={onPatched} />}
      </div>
    </Modal>
  );
}

function OverviewTab({
  ticket,
  onPatched,
  onDeleted,
}: {
  ticket: BugReport;
  onPatched: (t: BugReport) => void;
  onDeleted: (id: string) => void;
}) {
  const toast = useToast();
  const [notes, setNotes] = useState(ticket.notes || "");
  const [busy, setBusy] = useState(false);

  const setStatus = async (status: BugStatus) => {
    if (status === ticket.status) return;
    try {
      onPatched(await bugReportApi.update(ticket.id, { status }));
      toast("ok", `Status → ${STATUS_LABEL[status]}`);
    } catch (e) {
      toast("err", e instanceof AdminApiError ? e.message : "Status change failed");
    }
  };

  return (
    <div className="space-y-4">
      {/* The report itself */}
      <div className="rounded-(--radius-field) bg-ink-900 p-3.5 text-sm whitespace-pre-wrap">
        {ticket.description}
      </div>

      {/* Status control */}
      <div>
        <span className="text-xs font-medium text-fg-mute">Status</span>
        <div className="mt-1.5 flex gap-1.5">
          {(["open", "ack", "fixed", "wontfix"] as BugStatus[]).map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={`rounded-(--radius-field) px-3 py-1.5 text-xs font-medium transition ${
                ticket.status === s
                  ? "bg-ink-700 text-fg ring-1 ring-rose-500/50"
                  : "bg-ink-900 text-fg-mute hover:bg-ink-850"
              }`}
            >
              {STATUS_LABEL[s]}
            </button>
          ))}
        </div>
        <p className="mt-1.5 text-[11px] text-fg-faint">
          Fixed / won&rsquo;t fix locks the reporter&rsquo;s replies until reopened.
        </p>
      </div>

      {/* Meta grid */}
      <dl className="grid grid-cols-2 gap-x-6 gap-y-2.5 rounded-(--radius-field) bg-ink-900 p-3.5 text-xs sm:grid-cols-3">
        {(
          [
            ["Entity", ticket.entity],
            ["Tier", ticket.tier],
            ["Category", ticket.category],
            ["Plugin", ticket.pluginVersion ? `v${ticket.pluginVersion}` : "—"],
            ["Received", timeAgo(ticket.receivedAt)],
            ["License state", ticket.licenseState || "—"],
          ] as const
        ).map(([k, v]) => (
          <div key={k}>
            <dt className="text-fg-faint">{k}</dt>
            <dd className="mt-0.5 text-fg-mute">{v || "—"}</dd>
          </div>
        ))}
        <div className="col-span-2 sm:col-span-3">
          <dt className="text-fg-faint">Machine</dt>
          <dd className="mt-0.5 truncate font-mono text-[11px] text-fg-mute">{ticket.machine || "—"}</dd>
        </div>
        <div className="col-span-2 flex items-center justify-between sm:col-span-3">
          <div className="min-w-0">
            <dt className="text-fg-faint">Ticket id</dt>
            <dd className="mt-0.5 truncate font-mono text-[11px] text-fg-mute">{ticket.id}</dd>
          </div>
          <CopyButton text={ticket.id} />
        </div>
      </dl>

      {/* Triage notes (yours — never shown to the reporter) */}
      <label className="block">
        <span className="text-xs font-medium text-fg-mute">Triage notes (private)</span>
        <textarea
          className="field mt-1.5 min-h-20 resize-y text-[13px]"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </label>

      <div className="flex items-center justify-between">
        <button
          className="btn-danger !px-2.5"
          onClick={async () => {
            if (!confirm("Delete this ticket? This can't be undone.")) return;
            try {
              await bugReportApi.remove(ticket.id);
              toast("ok", "Ticket deleted");
              onDeleted(ticket.id);
            } catch (e) {
              toast("err", e instanceof AdminApiError ? e.message : "Delete failed");
            }
          }}
        >
          <Trash2 className="h-3.5 w-3.5" /> Delete
        </button>
        <button
          className="btn-primary"
          disabled={busy || notes === (ticket.notes || "")}
          onClick={async () => {
            setBusy(true);
            try {
              onPatched(await bugReportApi.update(ticket.id, { notes }));
              toast("ok", "Notes saved");
            } catch (e) {
              toast("err", e instanceof AdminApiError ? e.message : "Save failed");
            } finally {
              setBusy(false);
            }
          }}
        >
          Save notes
        </button>
      </div>
    </div>
  );
}

function ChatTab({
  ticket,
  closed,
  onPatched,
}: {
  ticket: BugReport;
  closed: boolean;
  onPatched: (t: BugReport) => void;
}) {
  const toast = useToast();
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const comments = ticket.comments ?? [];
  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => endRef.current?.scrollIntoView({ block: "end" }), [comments.length]);

  const send = async () => {
    const body = text.trim();
    if (!body) return;
    setSending(true);
    try {
      onPatched(await bugReportApi.comment(ticket.id, body));
      setText("");
    } catch (e) {
      toast("err", e instanceof AdminApiError ? e.message : "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2.5">
        {comments.length === 0 ? (
          <Empty title="No messages yet." hint="Anything you send shows up in the buyer's Studio." />
        ) : (
          comments.map((c, i) => (
            <div key={i} className={`flex ${c.author === "admin" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[80%] rounded-xl px-3.5 py-2 text-sm ${
                  c.author === "admin" ? "bg-rose-500/15 text-fg" : "bg-ink-900 text-fg"
                }`}
              >
                <div className="mb-0.5 text-[10px] tracking-wide text-fg-faint uppercase">
                  {c.author === "admin" ? "You" : ticket.entity || "Operator"} · {timeAgo(c.ts)}
                </div>
                <div className="whitespace-pre-wrap">{c.body}</div>
              </div>
            </div>
          ))
        )}
        <div ref={endRef} />
      </div>

      {closed && (
        <p className="flex items-center gap-1.5 rounded-(--radius-field) bg-ink-900 px-3 py-2 text-xs text-fg-faint">
          <Lock className="h-3 w-3" /> Closed — the reporter can read but not reply. You still can.
        </p>
      )}
      <div className="flex items-end gap-2">
        <textarea
          className="field min-h-16 resize-y"
          placeholder="Reply to the reporter…"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <button className="btn-primary" disabled={sending || !text.trim()} onClick={send}>
          <Send className="h-3.5 w-3.5" /> Send
        </button>
      </div>
    </div>
  );
}

function BuildsTab({
  ticket,
  onPatched,
}: {
  ticket: BugReport;
  onPatched: (t: BugReport) => void;
}) {
  const toast = useToast();
  const [jar, setJar] = useState<File | null>(null);
  const [signature, setSignature] = useState("");
  const [busy, setBusy] = useState(false);

  return (
    <div className="space-y-4">
      {ticket.build ? (
        <div className="rounded-(--radius-field) bg-ink-900 p-3.5">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="truncate text-sm font-medium">{ticket.build.filename}</div>
              <div className="mt-0.5 text-xs text-fg-faint">
                {humanSize(ticket.build.size)} · uploaded {timeAgo(ticket.build.uploadedAt)}
              </div>
              <div className="mt-1.5 truncate font-mono text-[10px] text-fg-faint">
                sha256 {ticket.build.sha256}
              </div>
            </div>
            <button
              className="btn-danger shrink-0 !px-2.5"
              disabled={busy}
              onClick={async () => {
                if (!confirm("Remove the attached build?")) return;
                setBusy(true);
                try {
                  onPatched(await bugReportApi.deleteBuild(ticket.id));
                  toast("ok", "Build removed");
                } catch (e) {
                  toast("err", e instanceof AdminApiError ? e.message : "Remove failed");
                } finally {
                  setBusy(false);
                }
              }}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
          <p className="mt-2.5 text-[11px] text-fg-faint">
            The buyer sees “Hotfix available” on this ticket; their plugin verifies the signature
            before staging it.
          </p>
        </div>
      ) : (
        <Empty title="No build attached." hint="Attach a signed hotfix JAR for this buyer below." />
      )}

      <div className="rounded-(--radius-field) bg-ink-900 p-3.5">
        <div className="text-xs font-medium text-fg-mute">
          {ticket.build ? "Replace build" : "Attach a signed build"}
        </div>
        <div className="mt-2.5 space-y-2.5">
          <input
            type="file"
            accept=".jar"
            className="block w-full text-xs text-fg-mute file:mr-3 file:cursor-pointer file:rounded-(--radius-field) file:border-0 file:bg-ink-700 file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-fg hover:file:bg-ink-600"
            onChange={(e) => setJar(e.target.files?.[0] ?? null)}
          />
          <textarea
            className="field min-h-14 resize-y font-mono text-[11px]"
            placeholder="ed25519 signature (base64) — from the Sign page or sign-jar.mjs"
            value={signature}
            onChange={(e) => setSignature(e.target.value)}
          />
          <div className="flex items-center justify-between">
            <Link to="/sign" className="inline-flex items-center gap-1.5 text-xs text-rose-400 hover:text-rose-300">
              <FileSignature className="h-3.5 w-3.5" /> Sign locally →
            </Link>
            <button
              className="btn-primary"
              disabled={busy || !jar || !signature.trim()}
              onClick={async () => {
                if (!jar) return;
                setBusy(true);
                try {
                  onPatched(await bugReportApi.uploadBuild(ticket.id, jar, signature.trim()));
                  toast("ok", "Build attached");
                  setJar(null);
                  setSignature("");
                } catch (e) {
                  toast("err", e instanceof AdminApiError ? e.message : "Upload failed");
                } finally {
                  setBusy(false);
                }
              }}
            >
              {busy ? "Uploading…" : "Attach"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
